import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQzOTkxNjV8MA&ixlib=rb-4.1.0&q=85";

// Empty in dev/any deployment that hasn't set up a Google Cloud OAuth Client ID yet
// — the button below falls back to a disabled "coming soon" state, same convention
// as every other optional integration in this app (Stripe, OpenAI, Apify, ...).
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const { login, verify2fa, loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const googleButtonRef = useRef(null);

  const finishGoogleLogin = async (response) => {
    try {
      const res = await loginWithGoogle(response.credential);
      if (res?.requires_2fa) {
        setChallenge(res.challenge);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail) || err.message);
    }
  };

  // Google Identity Services — loaded only on this page (not globally), renders
  // its own real button into googleButtonRef once the script is ready. The
  // callback hands us a signed JWT (response.credential) that the backend
  // verifies itself; we never touch Google's OAuth endpoints directly.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || challenge) return;
    const init = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: finishGoogleLogin });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline", size: "large", width: 336, text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) {
      init();
      return;
    }
    const existing = document.getElementById("google-identity-script");
    if (existing) { existing.addEventListener("load", init); return; }
    const script = document.createElement("script");
    script.id = "google-identity-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = init;
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) {
        setChallenge(res.challenge);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2fa(challenge, code);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={AUTH_BG} alt="Architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55 grain-overlay" />
        <div className="absolute bottom-0 p-12">
          <Compass className="h-7 w-7 text-[hsl(var(--success))] mb-6" />
          <h2 className="font-display text-4xl text-white leading-tight max-w-md">
            {t("hero_line")}
          </h2>
          <p className="text-white/60 mt-4 text-sm max-w-sm">YieldPilot · Portugal → Europe</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Compass className="h-6 w-6 text-[hsl(var(--success))]" />
            <span className="font-display text-2xl">YieldPilot</span>
          </div>
          <h1 className="font-display text-3xl mb-1">{t("login_title")}</h1>
          <p className="text-muted-foreground text-sm mb-8">{t("login_sub")}</p>

          {challenge ? (
            <form onSubmit={submit2fa} className="space-y-4" data-testid="twofa-form">
              <div className="space-y-2">
                <Label htmlFor="code">{t("twofa_code")}</Label>
                <Input id="code" inputMode="numeric" required autoFocus value={code}
                  onChange={(e) => setCode(e.target.value)} data-testid="twofa-code"
                  className="rounded-sm tracking-[0.4em] text-center font-mono text-lg" placeholder="000000" maxLength={6} />
                <p className="text-xs text-muted-foreground">{t("twofa_hint")}</p>
              </div>
              <Button type="submit" disabled={loading} data-testid="twofa-submit"
                className="w-full rounded-sm active:scale-95 transition-transform">
                {loading ? "..." : t("login")}
              </Button>
            </form>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="rounded-sm"
                placeholder="you@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="rounded-sm"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-sm active:scale-95 transition-transform"
            >
              {loading ? "..." : t("login")}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase">{t("or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          {GOOGLE_CLIENT_ID ? (
            <div ref={googleButtonRef} data-testid="google-login" className="flex justify-center" />
          ) : (
            <Button
              variant="outline"
              disabled
              data-testid="google-login"
              title={t("google_coming_soon")}
              className="w-full rounded-sm gap-2"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
              </svg>
              {t("continue_google")} <span className="text-muted-foreground">{t("google_coming_soon")}</span>
            </Button>
          )}

          <p className="text-sm text-muted-foreground mt-8 text-center">
            {t("no_account")}{" "}
            <Link to="/register" data-testid="go-register" className="text-foreground underline underline-offset-4">
              {t("register")}
            </Link>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
